import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import {
  BiData,
  ClickDownloadButtonBiEvent,
  ClickReportButtonBiEvent,
  ClickShareBiEvent,
  ClickSocialShareBiEvent,
  GiveBadgeAwardBiEvent,
  PureBiData,
  ThumbClickCommentBiEvent,
  UnlistItemOpenBiEvent,
  UnlistItemConfirmBiEvent,
  EditWithMuroBiEvent,
  BiContextProvidedValue,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import StandardMenu from '@wix/da-ds/pkg/Dropdown/MenuPreset/StandardMenu';
import { getDeviationResourceType } from '../../../../DeviationViews/_helpers';
import { useVerification } from '../../../../VerificationPopup/withVerification';
import { ActionBarContext } from '../../../ActionBarContext';
import { StandardDeviationElementPlacement } from '../../../ElementPlacementContext/variants/standard/types';
import { useElementPlacementForArea } from '../../../ElementPlacementContext/utils';
import { StandardDeviationElementPlacementContext } from '../../../ElementPlacementContext/variants/standard/context';
import { OnUnpublishCompleteAction } from '../../../../redux/deviations/actionCreators';
import {
  useFreeDownloadAction,
  usePremiumDownloadAction,
} from '../../DownloadButton/hooks';
import { UseActionsWithPopupsReturnType } from '../hooks';
import { useCopyDeviationLinkClick } from '../../../../utils/hooks/useCopyLinkClick';
import { AwardBadgeSource } from '../../../../coreBadge/types';
import { URLS } from '@wix/da-url';
import { useUnpublishFlow } from '../../../../utils/hooks/useUnpublishFlow';
import { MuroDepContext } from '@wix/da-react-context/pkg/MuroDepContext';
import { usePushModal } from '../../../../utils/hooks/usePushModal';
import { ModalType } from '../../../../Modals/redux/types';

import s from './MoreMenuContent.scss';

export interface Props {
  deviation: PapiDeviation;
  isOwner: boolean;
  showReportModal: (deviation: PapiDeviation) => void;
  shareDeviationToPost: (deviation: PapiDeviation) => void;
  showAwardBadgePopup: (
    deviation: PapiDeviation,
    source: AwardBadgeSource
  ) => void;
  closeTooltip?: () => void;
  actionsWithPopups: UseActionsWithPopupsReturnType;
  onCommentClick: (deviation: PapiDeviation) => void;
}

/**
 * Takes an array of actions that show popups and returns them with their dropdown entry's `onClick` callback
 * altered to call the supplied {@link callback} when executed.
 */
function withExtraCallbackInActionOnClick(
  actionsWithPopups: UseActionsWithPopupsReturnType,
  callback: () => void
): UseActionsWithPopupsReturnType {
  return Object.entries(actionsWithPopups).reduce(
    (accumulator, [key, value]) => ({
      ...accumulator,
      [key]: value
        ? {
            ...value,
            action: {
              ...value.action,
              onClick: e => {
                value.action.onClick && value.action.onClick(e);
                callback();
              },
            },
          }
        : value,
    }),
    {}
  ) as UseActionsWithPopupsReturnType;
}

// This is used only on desktop
const MoreMenuContent: React.FC<Props> = ({
  deviation,
  isOwner,
  closeTooltip = () => null,
  showReportModal,
  shareDeviationToPost,
  showAwardBadgePopup,
  onCommentClick,
  actionsWithPopups,
}) => {
  const { t } = useTranslation();
  const actionBarType = useContext(ActionBarContext);
  const { restrictToVerifiedAndLoggedIn } = useVerification();
  const { onCopyLinkClick } = useCopyDeviationLinkClick(deviation);
  const elementPlacementMap = useElementPlacementForArea(
    actionBarType === 'primary'
      ? StandardDeviationElementPlacement.PrimaryBarMenu
      : StandardDeviationElementPlacement.SecondaryBarMenu,
    StandardDeviationElementPlacementContext
  );
  const { MiniComponent } = useContext(MuroDepContext);
  const pushMuroModal = usePushModal(ModalType.MINI_MURO);

  const initiateUnpublishFlow = useUnpublishFlow({
    deviation,
    onClose: closeTooltip,
    onCompleteAction: OnUnpublishCompleteAction.ReloadStashPage,
    confirmBtnBiData: BiData<UnlistItemConfirmBiEvent>({
      evid: 359,
      itemid: deviation.deviationId,
      typeid: getDeviationResourceType(deviation),
      sectionname: 'action_bar',
    }),
  });
  const freeDownloadAction = useFreeDownloadAction(deviation, isOwner);
  const premiumDownloadAction = usePremiumDownloadAction(deviation, isOwner);

  const actionsWithPopupsAndAugmentedOnClick = withExtraCallbackInActionOnClick(
    actionsWithPopups,
    closeTooltip
  );

  const handleCommentClick = useCallback(() => {
    onCommentClick(deviation);
  }, [onCommentClick, deviation]);

  return (
    <StandardMenu
      className={s['menu']}
      id="actionbar-more-menu"
      items={[
        elementPlacementMap.comment && {
          value: 'comment',
          label: t('actionbar.comment.label'),
          onClick: handleCommentClick,
          biData: BiData<ThumbClickCommentBiEvent>({
            evid: 105,
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId,
            creatorid: deviation.author?.useridUuid ?? '',
          }),
        },
        elementPlacementMap.premiumDownload &&
          premiumDownloadAction.hasAction && {
            value: 'download',
            label: t('actionbar.premiumDownload'),
            link: premiumDownloadAction.href,
            onClick: () => {
              premiumDownloadAction.onClick && premiumDownloadAction.onClick();
              closeTooltip();
            },
            biData: PureBiData<ClickDownloadButtonBiEvent>({
              evid: 350,
              ispaiddownload: 1,
              deviationid: deviation.deviationId,
              sectionname: 'action_bar',
            }),
          },
        elementPlacementMap.freeDownload &&
          freeDownloadAction.hasAction && {
            value: 'download',
            label: t('actionbar.freeDownload'),
            link: freeDownloadAction.href,
            onClick: () => {
              freeDownloadAction.onClick && freeDownloadAction.onClick();
              closeTooltip();
            },
            biData: PureBiData<ClickDownloadButtonBiEvent>({
              evid: 350,
              ispaiddownload: 0,
              deviationid: deviation.deviationId,
              sectionname: 'action_bar',
            }),
          },
        elementPlacementMap.viewOrderHistory && {
          value: 'viewOrderHistory',
          label: t('actionbar.viewOrderHistory'),
          link: `${URLS.CUSTOMER_DASHBOARD}?subproductid=${deviation.extended?.pcp?.subproductId}`,
        },
        actionsWithPopupsAndAugmentedOnClick.addToGroup?.action,
        elementPlacementMap.awardBadge && {
          value: 'awardBadge',
          label: t('actionbar.awardBadge'),
          onClick: () => {
            showAwardBadgePopup(deviation, AwardBadgeSource.ACTIONBAR_MORE);
            closeTooltip();
          },
          biData: BiData<GiveBadgeAwardBiEvent>({
            evid: 320,
            sectionname: 'deviation',
            typeid: deviation.typeId,
            itemid: deviation.deviationId,
          }),
        },
        actionsWithPopupsAndAugmentedOnClick.privateCollection?.action,
        elementPlacementMap.shareCopyLink && {
          value: 'copyLink',
          label: t('actionbar.copyLink'),
          onClick: onCopyLinkClick,
          biData: BiData<ClickShareBiEvent>({
            evid: 355,
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId,
            deviationid: deviation.deviationId,
          }),
        },
        elementPlacementMap.shareToPost && {
          value: 'shareToPost',
          label: t('actionbar.shareToPost'),
          onClick: restrictToVerifiedAndLoggedIn(
            ReferralType.SHARE_TO_POST,
            'submitstatus',
            () => shareDeviationToPost(deviation)
          ),
          biData: BiData<ClickSocialShareBiEvent>({
            evid: 356,
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId,
            social_share: 'da_post',
            deviationid: deviation.deviationId,
          }),
        },
        elementPlacementMap.report && {
          value: 'report',
          label: t('actionbar.report'),
          biData: BiData<ClickReportButtonBiEvent>({
            evid: 20,
            modal_version: 2,
            deviationid: deviation.deviationId,
          }),
          onClick: () => {
            showReportModal(deviation);
            closeTooltip();
          },
        },
        elementPlacementMap.unpublish && {
          value: 'unpublish',
          label: t('actionbar.unpublish'),
          onClick: initiateUnpublishFlow,
          biData: BiData<UnlistItemOpenBiEvent>({
            evid: 358,
            sectionname: 'deviation',
            typeid: getDeviationResourceType(deviation),
            itemid: deviation.deviationId,
          }),
        },
        elementPlacementMap.draw && {
          label: t('actionbar.draw.edit'),
          biData: BiData<EditWithMuroBiEvent>({
            evid: 926,
            deviationid: deviation.deviationId,
            sectionname: BiContextProvidedValue,
            page_itemid: BiContextProvidedValue,
            page_typeid: BiContextProvidedValue,
          }),
          onClick: () => {
            if (MiniComponent) {
              pushMuroModal({
                miniComponent: MiniComponent,
                deviation: deviation,
              });
            }
          },
        },
      ]}
    />
  );
};
MoreMenuContent.displayName = 'MoreMenuContent';

export default MoreMenuContent;
